/**
 * The container a document came out of, as the table's second line under NAME says it.
 *
 * Two containers reach the same line. A zip dropped on the Documents screen is never stored —
 * the archive's bytes are thrown away and each selected entry becomes an ordinary document, so
 * `sourceArchive` is the only thing left that says the file arrived inside `report.zip` under
 * `germany/2024`. A workbook that carries files as icons *stays* a document, so a child's
 * container is a row of its own and its name is a link to it.
 *
 * One function answers both, because the line, the `title` text and the free-text filter must
 * agree about what a row says it came from. The wording of each case has exactly one body: the
 * archive's is below, the embedding's is `formatEmbeddedProvenance` in `@repo/shared`, which the
 * backend and the read tools label a child with too. Both bodies join on `PROVENANCE_JOIN`, the
 * same shared constant, so the two lines read as one family.
 */

import { type EmbeddedProvenance, formatEmbeddedProvenance, PROVENANCE_JOIN } from "@repo/shared";

export type SourceArchive = {
  /** The archive's own filename, as dropped — `report.zip`. */
  zipName: string;
  /** The full `/`-separated path inside the archive, *including* the entry's filename. */
  entryPath: string;
};

/**
 * The folder part of an entry path — `germany/2024` for `germany/2024/sales.pdf` — or `null`
 * for an entry sitting at the archive root.
 *
 * The backend guarantees `entryPath` ends with the file's own name and carries no leading
 * slash or `..` segment (`validateSourceArchive`), so this is a plain last-slash split. A row
 * written before that validation existed, or hand-patched, degrades to "no folder" rather than
 * drawing something misleading.
 */
export function archiveEntryFolderPath(entryPath: string): string | null {
  const lastSlash = entryPath.lastIndexOf("/");
  if (lastSlash <= 0) return null;
  const folder = entryPath.slice(0, lastSlash);
  return folder.length > 0 ? folder : null;
}

/**
 * The muted second line under the file's name: `report.zip › germany/2024`, or just
 * `report.zip` for an entry that sat at the archive root.
 *
 * The filename is deliberately *not* repeated — it is the line above.
 */
export function formatArchiveProvenance(sourceArchive: SourceArchive): string {
  const folder = archiveEntryFolderPath(sourceArchive.entryPath);
  return folder === null
    ? sourceArchive.zipName
    : `${sourceArchive.zipName} ${PROVENANCE_JOIN} ${folder}`;
}

/**
 * Said of a child whose parent has been captured again without the object it was extracted
 * from. The file is still here and still cited; what changed is that the workbook no longer
 * carries it, and the line says that rather than pointing at an anchor that has gone.
 */
const NO_LONGER_IN = "no longer in ";

/** Which container a row is drawn from. A row has one or neither, never both. */
export type DocumentProvenanceSource =
  | { kind: "archive"; sourceArchive: SourceArchive }
  | { kind: "embedded"; provenance: EmbeddedProvenance };

/**
 * One run of the line. Only a `document` segment is a link: the archive is gone and its name is
 * text, while a parent workbook is a row of this same table.
 */
export type DocumentProvenanceSegment =
  | { kind: "text"; text: string }
  | { kind: "document"; text: string; artifactId: string };

export type DocumentProvenanceLine = {
  /** The whole line, for the `title` tooltip and for anything that matches it as one string. */
  text: string;
  /** The same line, split where the part that links to another row begins and ends. */
  segments: DocumentProvenanceSegment[];
};

/** `report.zip › germany/2024`, or `HLD_Details.xlsx › Sheet2!P32` with the parent linked. */
export function formatDocumentProvenance(source: DocumentProvenanceSource): DocumentProvenanceLine {
  if (source.kind === "archive") {
    const text = formatArchiveProvenance(source.sourceArchive);
    return { text, segments: [{ kind: "text", text }] };
  }
  return embeddedProvenanceLine(source.provenance);
}

function embeddedProvenanceLine(provenance: EmbeddedProvenance): DocumentProvenanceLine {
  const parent: DocumentProvenanceSegment = {
    kind: "document",
    text: provenance.parentName,
    artifactId: provenance.parentArtifactId,
  };
  if (provenance.supersededAt !== undefined) {
    const text = `${NO_LONGER_IN}${provenance.parentName}`;
    return { text, segments: [{ kind: "text", text: NO_LONGER_IN }, parent] };
  }
  const text = formatEmbeddedProvenance(provenance);
  // The shared formatter leads with the parent's name, so the rest of the line is what follows
  // it. Peeled off the finished string rather than rebuilt from the anchor: the separator and
  // the locator keep one body, and this only decides which run of it is a link. A name the line
  // does not start with — nothing produces one today — degrades to a line with no link at all,
  // which is better than a link over the wrong run of it.
  if (!text.startsWith(provenance.parentName)) return { text, segments: [{ kind: "text", text }] };
  const rest = text.slice(provenance.parentName.length);
  return { text, segments: rest.length > 0 ? [parent, { kind: "text", text: rest }] : [parent] };
}

/**
 * The one embedding a row's line is drawn from: a parent that still carries the file, and only
 * failing that one that no longer does.
 *
 * A child can sit at several anchors under one root and the row has one line. A live parent wins
 * because it is the one a reader can still open the file inside; "no longer in" is what is left
 * when every parent has been re-captured without it.
 */
export function primaryEmbeddedProvenance(
  entries: readonly EmbeddedProvenance[]
): EmbeddedProvenance | null {
  return entries.find((entry) => entry.supersededAt === undefined) ?? entries[0] ?? null;
}
